import { useEffect, useState } from "react";
import { ErrorNotice, useAction } from "../components/ui";
import { api, apiUrl, type CalendarFeed as Feed } from "../lib/api";
import { whenLast } from "../lib/dates";

/** The full address calendar apps subscribe to. Relative API bases get this site's origin. */
export const feedAddress = (token: string) => new URL(apiUrl(`/calendar/${token}.ics`), window.location.origin).toString();

/**
 * A private calendar address: subscribe once in Google Calendar, Outlook or Apple Calendar and the
 * interviews on your jobs appear there, moved and cancelled ones included. Nothing to connect.
 */
export function CalendarFeed() {
  const [feed, setFeed] = useState<Feed | null | undefined>(undefined);
  const [address, setAddress] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const load = useAction();
  const act = useAction();

  useEffect(() => void load.run(async () => setFeed((await api.calendarFeed()).feed)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = () =>
    void act.run(async () => {
      const { feed: f } = await api.createCalendarFeed();
      setFeed(f);
      setAddress(feedAddress(f.token));
      setCopied(false);
      setConfirmReplace(false);
    });

  return (
    <>
      <div className="page-head">
        <p className="label">Calendar</p>
        <h1 className="display">
          Interviews in <em>your calendar</em>.
        </h1>
        <p className="lede">
          Subscribe once and the interviews on your jobs show up in Google Calendar, Outlook or Apple Calendar. When you move or cancel one
          here, your calendar follows.
        </p>
      </div>

      <section className="section" aria-labelledby="feed-h">
        <div className="section-head">
          <h2 id="feed-h" className="label">
            Your calendar address
          </h2>
        </div>
        <ErrorNotice error={load.error ?? act.error} />
        {address ? (
          <div className="form copy-block">
            <p className="notice ok">Add this to your calendar now. It won't be shown again, but you can always make a new one.</p>
            <div className="copy-row">
              <code>{address}</code>
              <button type="button" className="btn secondary" onClick={() => void navigator.clipboard.writeText(address).then(() => setCopied(true))}>
                {copied ? "Copied" : "Copy address"}
              </button>
            </div>
            <div className="button-row">
              <a className="link-btn" href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(address.replace(/^https:/, "webcal:"))}`} target="_blank" rel="noopener">
                Add to Google Calendar
              </a>
              <a className="link-btn" href={address.replace(/^https?:/, "webcal:")}>
                Open in Apple Calendar or Outlook
              </a>
              <button type="button" className="link-btn" onClick={() => setAddress(null)}>
                Done
              </button>
            </div>
          </div>
        ) : feed === undefined ? (
          <p className="label">Loading…</p>
        ) : feed === null ? (
          <div className="button-row">
            <button type="button" className="btn" disabled={act.pending} onClick={create}>
              Make my calendar address
            </button>
          </div>
        ) : (
          <>
            <p>
              Your address is on. Made {whenLast(feed.createdAt)} ·{" "}
              {feed.lastFetchedAt ? `your calendar last checked it ${whenLast(feed.lastFetchedAt)}` : "no calendar has checked it yet"}.
            </p>
            <div className="button-row">
              {confirmReplace ? (
                <>
                  <span className="label alert">The old address stops working, so you'll need to subscribe again.</span>
                  <button type="button" className="btn secondary" disabled={act.pending} onClick={create}>
                    Make a new address
                  </button>
                  <button type="button" className="link-btn" onClick={() => setConfirmReplace(false)}>
                    Keep the old one
                  </button>
                </>
              ) : (
                <button type="button" className="link-btn" onClick={() => setConfirmReplace(true)}>
                  Make a new address
                </button>
              )}
              <button
                type="button"
                className="link-btn danger"
                disabled={act.pending}
                onClick={() =>
                  void act.run(async () => {
                    await api.revokeCalendarFeed();
                    setFeed(null);
                  })
                }
              >
                Turn off
              </button>
            </div>
          </>
        )}
      </section>

      <section className="section" aria-labelledby="how-h">
        <div className="section-head">
          <h2 id="how-h" className="label">
            Adding it by hand
          </h2>
        </div>
        <ul className="plain-list">
          <li>
            <strong>Google Calendar</strong> on a computer: Other calendars, +, From URL. Paste the address.
          </li>
          <li>
            <strong>Outlook</strong>: Add calendar, Subscribe from web. Paste the address and name it KuduRMS.
          </li>
          <li>
            <strong>Apple Calendar</strong>: File, New Calendar Subscription. Paste the address.
          </li>
        </ul>
      </section>

      <section className="section" aria-labelledby="know-h">
        <div className="section-head">
          <h2 id="know-h" className="label">
            Good to know
          </h2>
        </div>
        <ul className="plain-list">
          <li>Calendars check for changes on their own schedule: Outlook and Apple every hour or so, Google every few hours. For a same-day change, tell people directly too.</li>
          <li>It shows interviews on jobs you own or that you booked, from the last two months onwards.</li>
          <li>Anyone with the address can see the candidates' names and interview times, so keep it to yourself. If it gets out, make a new one.</li>
          <li>Interview notes stay out of it, since your calendar provider keeps a copy.</li>
        </ul>
      </section>
    </>
  );
}
